import * as THREE from 'three'
import type RAPIER from '@dimforge/rapier3d-compat'
import { BspLevelLoader, type BspLoadedLevel, type BspLevelData } from './bsp-loader'
import { buildGalleryLevel } from './gallery-level'
import { buildRoguesGalleryLevel } from './rogues-gallery-level'
import rawTutorialLevel from '../../assets/levels/tutorial/tutorial-level.json'
import rawAb00Level from '../../assets/levels/ab00/ab00-level.json'
import rawEb04Level from '../../assets/levels/eb04/eb04-level.json'

export type { BspLoadedLevel, BspLevelData }

const LEVEL_REGISTRY: Record<string, BspLevelData> = {
  ab00: rawAb00Level as unknown as BspLevelData,
  eb04: rawEb04Level as unknown as BspLevelData,
  tutorial: rawTutorialLevel as unknown as BspLevelData,
  training: rawTutorialLevel as unknown as BspLevelData,
}

// Auto-discover any additional extracted level JSON files
const dynamicLevels = import.meta.glob('../../assets/levels/**/*.json', { eager: true }) as Record<
  string,
  { default: BspLevelData } | BspLevelData
>
for (const filePath in dynamicLevels) {
  const mod = dynamicLevels[filePath]
  const data = (mod && 'default' in mod ? mod.default : mod) as BspLevelData
  if (data && data.level) {
    LEVEL_REGISTRY[data.level.toLowerCase()] = data
  }
}

export interface DiscoveredSector {
  id: string
  name: string
  description: string
  verts: number
  area: string
  objects: number
  badge?: string
}

const SECTOR_FRIENDLY_METADATA: Record<string, { name: string; desc: string; badge?: string }> = {
  tutorial: { name: 'Training Proving Grounds', desc: 'Authentic 1998 combat proving grounds with blast doors & target gallery', badge: 'TUTORIAL' },
  ab00: { name: 'Airlock Chamber (ab00)', desc: 'Transition airlock junction between facility wings', badge: 'AIRLOCK' },
  eb04: { name: 'Elevator Shaft Bay 4 (eb04)', desc: 'Vertical kinematic lift transport tube with sliding blast door', badge: 'ELEVATOR' },
  eb06: { name: 'Tower Access Node (eb06)', desc: 'Multi-level vertical maintenance column', badge: 'TOWER' },
  ab02: { name: 'Corridor Node (ab02)', desc: 'High-density interconnect corridor' },
  do09: { name: 'Perimeter Sector (do09)', desc: 'Outer perimeter facility wing' },
  boiler: { name: 'Boiler Core Facility', desc: 'Heavy industrial power generator core', badge: 'POWER' },
  city1: { name: 'City Zone Alpha (city1)', desc: 'Urban industrial perimeter complex' },
  city2: { name: 'City Zone Beta (city2)', desc: 'Urban industrial core plaza' },
  mt01: { name: 'Mountain Facility Node 1', desc: 'Subterranean mountain installation' },
}

export function getDiscoveredSectors(): DiscoveredSector[] {
  const list: DiscoveredSector[] = []

  // Add 3D Prop & Entity Gallery Exhibition
  list.push({
    id: 'gallery',
    name: '3D Prop & Entity Gallery',
    description: 'Museum showroom displaying all 185 unique cracked 3D vertex hulls, doors, crates, and lifts',
    verts: 1850,
    area: '2160.0 m²',
    objects: 185,
    badge: 'EXHIBIT',
  })

  // Add 2D Billboard Enemy Rogues' Gallery
  list.push({
    id: 'rogues',
    name: "Enemy Rogues' Gallery",
    description: 'Museum showroom exhibiting all 35 authentic animated 2D billboard enemies with 8-directional view tracking',
    verts: 280,
    area: '2160.0 m²',
    objects: 35,
    badge: 'ROGUES',
  })

  const keys = Object.keys(LEVEL_REGISTRY).sort()

  for (const id of keys) {
    if (id === 'training') continue // 'training' is alias for 'tutorial'
    const data = LEVEL_REGISTRY[id]
    const custom = SECTOR_FRIENDLY_METADATA[id]
    const verts = data.mesh_point_cloud?.vertex_count ?? data.mesh_point_cloud?.vertices?.length ?? 0
    const objects = data.placed_objects?.length ?? 0
    const bboxMin = data.mesh_point_cloud?.bbox_min
    const bboxMax = data.mesh_point_cloud?.bbox_max

    const getCoord = (v: any, idx: number, key: string) =>
      Array.isArray(v) ? v[idx] : v[key]

    let area = '0.0 m²'
    if (bboxMin && bboxMax) {
      const minX = getCoord(bboxMin, 0, 'x')
      const maxX = getCoord(bboxMax, 0, 'x')
      const minY = getCoord(bboxMin, 1, 'y')
      const maxY = getCoord(bboxMax, 1, 'y')
      const w = Math.abs(maxX - minX) * 0.05
      const d = Math.abs(maxY - minY) * 0.05
      area = `${(w * d).toFixed(1)} m²`
    }

    const name = custom?.name ?? `Sector ${id.toUpperCase()}`
    const description = custom?.desc ?? `Authentic .E3 sector volume with ${data.sectors?.length ?? 0} BSP partitions`
    const badge = custom?.badge ?? (objects > 0 ? `${objects} OBJ` : undefined)

    list.push({
      id,
      name,
      description,
      verts,
      area,
      objects,
      badge,
    })
  }

  // Add whitebox testing arena
  list.push({
    id: 'whitebox',
    name: 'Whitebox Sandbox',
    description: 'Testing arena for enemy AI, weapons, physics, and pathfinding',
    verts: 120,
    area: '450.0 m²',
    objects: 8,
    badge: 'SANDBOX',
  })

  return list
}

export async function buildBspLevel(
  scene: THREE.Scene,
  world: RAPIER.World,
  scale = 0.05,
  debugRegistry?: import('../debug/debug-materials').DebugMaterialRegistry,
  levelName = 'tutorial',
): Promise<BspLoadedLevel> {
  const cleanName = levelName.replace('.e3', '').toLowerCase()
  if (cleanName === 'gallery' || cleanName === 'props') {
    return await buildGalleryLevel(scene, world, debugRegistry)
  }
  if (
    cleanName === 'rogues' ||
    cleanName === 'rogues-gallery' ||
    cleanName === 'enemies' ||
    cleanName === 'enemy-gallery' ||
    cleanName === 'monsters'
  ) {
    return await buildRoguesGalleryLevel(scene, world, debugRegistry)
  }
  const loader = new BspLevelLoader(scene, world, scale, debugRegistry)
  const data = LEVEL_REGISTRY[cleanName] ?? LEVEL_REGISTRY['tutorial'] ?? (rawTutorialLevel as unknown as BspLevelData)
  return await loader.load(data)
}
